/**
 * 前端WebSocket通知管理器
 * 提供WebSocket方式的通知连接和管理
 */
const isBrowser = typeof window !== 'undefined';

const toWebSocketScheme = (url) => {
    if (!url) {
        return url;
    }

    return url.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
};

const stripTrailingSlash = (value = '') => value.endsWith('/') ? value.slice(0, -1) : value;

const shouldUseDevProxy = () => {
    if (!isBrowser) {
        return false;
    }

    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
        return true;
    }

    const origin = window.location.origin || '';
    if (/localhost|127\.0\.0\.1/.test(origin)) {
        return true;
    }

    const port = window.location.port;
    return !!(port && port !== '80' && port !== '443');
};

class WebSocketNotificationManager {
    constructor(options = {}) {
        this.rawBaseUrl = options.baseUrl || 'auto';
        this.baseUrl = null;
        this.userId = options.userId;
        this.websocket = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectInterval = 3000;
        this.heartbeatInterval = null;
        this.heartbeatPayload = options.heartbeatPayload || 'ping';
        this.heartbeatIntervalMs = options.heartbeatInterval || 30000; // 30秒发送一次心跳，减少服务器压力
        this.reconnectTimer = null;
        this.listeners = new Map();
        // 心跳监控
        this.lastHeartbeatTime = null;
        this.heartbeatCheckTimer = null;
        this.heartbeatTimeout = options.heartbeatTimeout || 90000; // 90秒没收到心跳才认为连接异常
        this.maxReconnectDelay = options.maxReconnectDelay || 30000;
        this.baseReconnectDelay = options.baseReconnectDelay || 1000;
        // 连接状态管理
        this.isReconnecting = false;
        this.lastReconnectTime = 0;
        this.networkStatus = 'unknown';
        this.pageVisible = true;
        
        // 绑定方法
        this.connect = this.connect.bind(this);
        this.disconnect = this.disconnect.bind(this);
        this.sendMessage = this.sendMessage.bind(this);
        
        // 初始化网络和页面状态监听
        this.initNetworkMonitoring();
    }

    /**
     * 初始化网络状态和页面可见性监听
     */
    initNetworkMonitoring() {
        if (!isBrowser) return;
        
        // 监听网络状态变化
        if ('ononline' in window) {
            window.addEventListener('online', () => {
                this.networkStatus = 'online';
                console.log('🌐 网络已连接，检查WebSocket状态');
                this.handleNetworkChange();
            });
            
            window.addEventListener('offline', () => {
                this.networkStatus = 'offline';
                console.log('🌐 网络已断开');
            });
        }
        
        // 监听页面可见性变化
        if ('visibilityState' in document) {
            document.addEventListener('visibilitychange', () => {
                const isVisible = document.visibilityState === 'visible';
                this.pageVisible = isVisible;
                console.log(`👁️ 页面${isVisible ? '可见' : '隐藏'}`);
                
                if (isVisible) {
                    // 页面重新可见时，检查连接状态
                    this.handlePageVisible();
                }
            });
        }
    }

    /**
     * 处理网络状态变化
     */
    handleNetworkChange() {
        if (this.networkStatus === 'online' && !this.isConnected && !this.isReconnecting) {
            console.log('🔄 网络恢复，尝试重连WebSocket');
            this.scheduleReconnect(this.userId);
        }
    }

    /**
     * 处理页面重新可见
     */
    handlePageVisible() {
        if (!this.isConnected && !this.isReconnecting && this.userId) {
            console.log('🔄 页面重新可见，检查WebSocket连接');
            // 延迟一点时间再检查，避免页面切换时的误判
            setTimeout(() => {
                this.checkConnection();
            }, 1000);
        }
    }

    getBaseUrl() {
        if (this.baseUrl) {
            return this.baseUrl;
        }

        let resolved = this.rawBaseUrl;

        if (!resolved || resolved === 'auto') {
            if (isBrowser) {
                if (shouldUseDevProxy()) {
                    resolved = '/knowledge-api';
                } else {
                    // 生产环境也需要添加 /knowledge-api 前缀
                    resolved = window.location.origin + '/knowledge-api';
                }
            } else {
                resolved = '';
            }
        }

        this.baseUrl = this.normalizeBaseUrl(resolved);
        return this.baseUrl;
    }

    normalizeBaseUrl(value) {
        if (!value) {
            return '';
        }

        if (value.startsWith('/')) {
            return stripTrailingSlash(value);
        }

        if (value.startsWith('ws://') || value.startsWith('wss://')) {
            return stripTrailingSlash(value);
        }

        if (value.startsWith('http://') || value.startsWith('https://')) {
            return stripTrailingSlash(toWebSocketScheme(value));
        }

        // treat as host without scheme
        return `ws://${stripTrailingSlash(value)}`;
    }

    buildWebSocketUrl(baseUrl, userId) {
        if (!baseUrl) {
            if (!isBrowser) {
                throw new Error('无法推断WebSocket地址: 缺少baseUrl并且不在浏览器环境');
            }
            const url = new URL('/ws/notification', window.location.origin);
            url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
            url.searchParams.set('userId', userId);
            return url.toString();
        }

        if (baseUrl.startsWith('/')) {
            if (!isBrowser) {
                throw new Error('相对路径的WebSocket地址需要浏览器环境');
            }
            const relativeBase = stripTrailingSlash(baseUrl);
            const url = new URL(`${relativeBase}/ws/notification`, window.location.origin);
            url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
            url.searchParams.set('userId', userId);
            return url.toString();
        }

        const sanitizedBase = stripTrailingSlash(baseUrl);
        return `${sanitizedBase}/ws/notification?userId=${encodeURIComponent(userId)}`;
    }

    /**
     * 建立WebSocket连接
     */
    connect(userId = this.userId) {
        if (!userId) {
            console.warn('WebSocket连接失败: 用户ID不能为空');
            return Promise.resolve({ error: '用户ID不能为空' });
        }

        // 如果正在重连中，避免重复连接
        if (this.isReconnecting) {
            console.log('🔄 正在重连中，跳过重复连接请求');
            return Promise.resolve({ error: '正在重连中' });
        }

        this.userId = userId;
        
        // 如果已有连接，先断开
        if (this.websocket) {
            this.disconnect();
        }

        return new Promise((resolve) => {
            const baseUrl = this.getBaseUrl();
            const url = this.buildWebSocketUrl(baseUrl, userId);
            console.log('正在建立WebSocket连接:', url);
            
            try {
                this.websocket = new WebSocket(url);
                
                // 连接超时处理
                const timeout = setTimeout(() => {
                    if (!this.isConnected) {
                        console.warn('WebSocket连接超时，将尝试重连');
                        this.websocket.close();
                        // 不抛出错误，而是尝试重连
                        this.scheduleReconnect(userId);
                        resolve({ error: '连接超时，已安排重连' });
                    }
                }, 10000);

                this.websocket.onopen = () => {
                    clearTimeout(timeout);
                    console.log('✅ WebSocket连接已建立');
                    this.isConnected = true;
                    this.isReconnecting = false;
                    this.reconnectAttempts = 0;
                    this.lastReconnectTime = 0;
                    this.clearReconnectTimer();
                    this.startHeartbeat();
                    this.startHeartbeatCheck(); // 启动心跳监控
                    this.emit('connected', { userId, url });
                    resolve({ userId, url });
                };

                this.websocket.onmessage = (event) => {
                    this.markHeartbeatReceived();

                    if (typeof event.data === 'string') {
                        const text = event.data.trim();
                        if (text.toLowerCase() === 'pong') {
                            this.emit('pong', { data: 'pong' });
                            return;
                        }
                    }

                    try {
                        // 兼容心跳与业务消息
                        const data = JSON.parse(event.data);
                        this.handleMessage(data);
                    } catch (e) {
                        this.emit('message', { data: event.data, raw: true });
                    }
                };

                this.websocket.onclose = (event) => {
                    clearTimeout(timeout);
                    console.log('WebSocket连接已关闭:', event.code, event.reason);
                    this.isConnected = false;
                    this.stopHeartbeat();
                    this.stopHeartbeatCheck(); // 停止心跳监控
                    this.emit('disconnected', { code: event.code, reason: event.reason });
                    
                    // 非正常关闭时尝试重连，不限制重试次数
                    if (event.code !== 1000) {
                        this.reconnectAttempts++;
                        console.log(`WebSocket连接关闭，尝试重连 (第${this.reconnectAttempts}次)...`);
                        this.scheduleReconnect(userId);
                    }
                };

                this.websocket.onerror = (event) => {
                    clearTimeout(timeout);
                    console.warn('WebSocket连接错误:', event);
                    this.isConnected = false;
                    this.stopHeartbeat();
                    this.stopHeartbeatCheck(); // 停止心跳监控
                    this.emit('error', event);
                    
                    // 连接错误时尝试重连，不限制重试次数，静默重连
                    this.reconnectAttempts++;
                    console.log(`WebSocket连接错误，尝试重连 (第${this.reconnectAttempts}次)...`);
                    this.scheduleReconnect(userId);
                };

            } catch (error) {
                console.warn('创建WebSocket连接失败:', error);
                // 不抛出错误，而是尝试重连
                this.scheduleReconnect(userId);
                resolve({ error: '创建连接失败，已安排重连' });
            }
        });
    }

    /**
     * 断开WebSocket连接
     */
    disconnect() {
        if (this.websocket) {
            console.log('断开WebSocket连接');
            this.websocket.close(1000, '主动断开');
            this.websocket = null;
        }
        this.isConnected = false;
        this.isReconnecting = false;
        this.stopHeartbeat();
        this.stopHeartbeatCheck(); // 停止心跳监控
        this.clearReconnectTimer();
        this.reconnectAttempts = 0;
        this.lastHeartbeatTime = null;
        this.lastReconnectTime = 0;
        this.emit('disconnected');
    }

    /**
     * 处理收到的消息
     */
    handleMessage(data) {
        if (data.type === 'AUTOMATION_NOTIFICATION') {
            // 将外层的 id 合并到 payload 中，确保 ID 不丢失
            const notificationData = data.payload || {}
            if (data.id && !notificationData.id && !notificationData.notificationId) {
                notificationData.id = data.id
            }
            // 同时传递其他可能需要的字段（type, createTime等）
            if (data.type && !notificationData.type) {
                notificationData.type = data.type
            }
            if (data.createTime && !notificationData.createTime) {
                notificationData.createTime = data.createTime
            }
            this.emit('notification', { data: notificationData })
        } else if (data.type === 'HEARTBEAT') {
            const now = Date.now();
            // 更新最后心跳时间
            this.lastHeartbeatTime = now;
            // 收到心跳后立即回复，保持连接活跃
            this.sendMessage({
                type: 'PONG',
                payload: 'pong',
                timestamp: new Date().toISOString()
            });
            this.emit('heartbeat', { data: data.payload });
        } else {
            this.emit('message', { data });
        }
    }

    /**
     * 开始心跳检测
     */
    startHeartbeat() {
        this.stopHeartbeat();

        if (!this.heartbeatPayload) {
            return;
        }

        this.heartbeatInterval = setInterval(() => {
            if (this.isConnected && this.websocket.readyState === WebSocket.OPEN) {
                this.sendMessage(this.heartbeatPayload);
            } else if (!this.isReconnecting) {
                console.warn('WebSocket连接已断开，尝试重连...');
                this.scheduleReconnect(this.userId);
            }
        }, this.heartbeatIntervalMs);
    }

    /**
     * 停止心跳检测
     */
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * 开始心跳监控（检测是否收到后端心跳）
     */
    startHeartbeatCheck() {
        this.stopHeartbeatCheck();
        this.lastHeartbeatTime = Date.now();
        
        this.heartbeatCheckTimer = setInterval(() => {
            if (this.lastHeartbeatTime) {
                const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeatTime;
                
                // 如果页面隐藏超过30秒，暂停心跳超时检查，避免误判
                if (!this.pageVisible && timeSinceLastHeartbeat > 30000) {
                    console.log('👁️ 页面隐藏中，暂停心跳超时检查');
                    return;
                }
                
                // 添加连接状态检查，避免误判
                if (timeSinceLastHeartbeat > this.heartbeatTimeout) {
                    // 再次确认 WebSocket 状态，如果连接正常就不关闭
                    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                        console.warn(`⚠️ 超过 ${this.heartbeatTimeout / 1000}秒 未收到心跳，主动关闭并重连`);
                        this.websocket.close();
                    }
                }
            }
        }, 10000); // 每10秒检查一次
    }

    /**
     * 停止心跳监控
     */
    stopHeartbeatCheck() {
        if (this.heartbeatCheckTimer) {
            clearInterval(this.heartbeatCheckTimer);
            this.heartbeatCheckTimer = null;
        }
        this.lastHeartbeatTime = null;
    }

    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    scheduleReconnect(userId) {
        if (this.isReconnecting) {
            console.log('🔄 已在重连中，跳过重复重连请求');
            return;
        }

        this.clearReconnectTimer();
        this.isReconnecting = true;
        
        // 使用指数退避算法计算延迟时间
        const delay = Math.min(this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
        
        // 避免过于频繁的重连，最小间隔5秒
        const minDelay = Math.max(delay, 5000);
        
        console.log(`🔄 下一次重连将在 ${minDelay}ms 后尝试 (第${this.reconnectAttempts + 1}次)`);
        
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.lastReconnectTime = Date.now();
            
            // 静默重连，不处理返回结果
            this.connect(userId).catch(() => {
                // 连接失败，重置重连状态，让下次重连继续
                this.isReconnecting = false;
            });
        }, minDelay);
    }

    /**
     * 发送消息
     */
    sendMessage(message) {
        if (!this.isConnected || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
            console.warn('WebSocket未连接，无法发送消息');
            return false;
        }
        
        try {
            const payload = typeof message === 'object' ? JSON.stringify(message) : message;
            this.websocket.send(payload);
            return true;
        } catch (error) {
            console.error('发送WebSocket消息失败:', error);
            return false;
        }
    }

    /**
     * 添加事件监听器
     */
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }

    /**
     * 移除事件监听器
     */
    off(event, callback) {
        if (this.listeners.has(event)) {
            const callbacks = this.listeners.get(event);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    /**
     * 触发事件
     */
    emit(event, data) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('事件回调执行失败:', error);
                }
            });
        }
    }

    markHeartbeatReceived() {
        this.lastHeartbeatTime = Date.now();
    }

    /**
     * 获取连接状态
     */
    getStatus() {
        return {
            isConnected: this.isConnected,
            userId: this.userId,
            readyState: this.websocket ? this.websocket.readyState : null,
            reconnectAttempts: this.reconnectAttempts,
            url: this.websocket ? this.websocket.url : null
        };
    }

    /**
     * 检查连接状态
     */
    checkConnection() {
        if (this.websocket) {
            const readyState = this.websocket.readyState;
            if (readyState === WebSocket.OPEN && !this.isConnected) {
                console.log('🔧 检测到连接状态不一致，正在修复...');
                this.isConnected = true;
                this.isReconnecting = false;
                this.reconnectAttempts = 0;
                this.lastReconnectTime = 0;
                this.startHeartbeat();
                this.startHeartbeatCheck();
                this.emit('connected', { userId: this.userId, url: this.websocket.url });
                return true;
            } else if (readyState !== WebSocket.OPEN && this.isConnected) {
                console.log('🔧 检测到连接已断开，更新状态');
                this.isConnected = false;
                this.isReconnecting = false;
                // 如果不在重连中，尝试重连
                if (!this.isReconnecting && this.userId) {
                    this.scheduleReconnect(this.userId);
                }
                return false;
            }
        } else if (this.isConnected) {
            console.log('🔧 检测到WebSocket对象不存在，更新状态');
            this.isConnected = false;
            this.isReconnecting = false;
            // 如果不在重连中，尝试重连
            if (!this.isReconnecting && this.userId) {
                this.scheduleReconnect(this.userId);
            }
            return false;
        }
        return this.isConnected;
    }
}

if (typeof window !== 'undefined') {
    window.WebSocketNotificationManager = WebSocketNotificationManager;
}

export default WebSocketNotificationManager;
